import { getStringValue, getRichOrStringValue } from '../../common';
import type { PasteMetadata, ClipboardItem, ListIssueFieldTransformed } from '../../common/types';

const getStringifiedRichFieldValue = (field: ListIssueFieldTransformed) => {
	const richValue = getRichOrStringValue(field.type, field);

	if (!richValue) {
		return null;
	}

	if (typeof richValue === 'string') {
		return { value: richValue };
	}

	return richValue;
};

const safelyAddRichValueToHtml = (
	html: string,
	richValue: ReturnType<typeof getStringifiedRichFieldValue>,
) => {
	try {
		const DOM = new DOMParser().parseFromString(html, 'text/html');
		DOM.body?.querySelector('div')?.setAttribute('data-field-value', JSON.stringify(richValue));
		return DOM.body?.innerHTML || '';
	} catch (e) {
		return html;
	}
};

export const getCellValue = (
	field: ListIssueFieldTransformed,
	issue?: {
		key?: string;
		issueId?: string;
		issueType?: {
			level?: number;
		};
	},
) => {
	const textValue = getStringValue(field.type, field);
	const richValue = getStringifiedRichFieldValue(field);

	if (!textValue) {
		return {};
	}

	const html = `<div data-type="list-cell" data-issue-type-level="${issue?.issueType?.level}" data-field-id="${field.fieldId}" data-field-type="${field.type}">${textValue}</div>`;

	return {
		html: richValue ? safelyAddRichValueToHtml(html, richValue) : html,
		plainText: textValue,
	};
};

export const addToClipboard = (plainText: string, html: string) => {
	const blobPlain = new window.Blob([plainText], { type: 'text/plain' });
	const blobHTML = new window.Blob([html], { type: 'text/html' });
	const clipboardItem = new window.ClipboardItem({
		'text/html': blobHTML,
		'text/plain': blobPlain,
	});

	return navigator.clipboard.write([clipboardItem]);
};

export const getFieldMetadataFromClipboard = (clipboard: ClipboardItem): PasteMetadata | null => {
	if (!clipboard?.html) {
		return null;
	}

	try {
		const clipboardDocument = new DOMParser().parseFromString(clipboard.html, 'text/html');
		const data = clipboardDocument.body?.querySelector('div')?.dataset;

		if (!data || !data.fieldType || !data.fieldId) {
			return null;
		}

		return {
			fieldType: data.fieldType,
			fieldId: data.fieldId,
			fieldValue: data.fieldValue ? JSON.parse(data.fieldValue) : null,
			sourceIssueTypeLevel: data.issueTypeLevel,
		};
	} catch (e) {
		return null;
	}
};

export const notImplemented = () => Promise.resolve(false);
